import { theme } from "styles/theme";

// 분기(button_branch) 플로우차트식 가로 컬럼 레이아웃 공용 sx —
// TaskEditorBranchFlow 컴포넌트가 사용한다 (편집 뷰 루트 + 미리보기 정적 모드).
//
// 구조: 분기 카드 → 중앙 stem → 상단 커넥터(가로바+stub) → 옵션 컬럼들
//       → filler(세로선) → 하단 커넥터 → 중앙 stem → 다음 블록
//
// 세로선(stem/stub/filler)은 모두 "width 2px + mx:auto 중앙 정렬 Box" 로
// 통일한다 — 가로바의 ::after borderLeft 등 보더 기반으로 섞어 그리면
// 중앙 계산이 1~2px 어긋나 선이 만나지 않는다.

const branchLineColor = theme.vars.palette.Border.Weak;
const branchLine = `2px solid ${branchLineColor}`;

// 분기 카드↔가로바, 가로바↔다음 블록 사이의 중앙 세로 stem.
export const taskEditorBranchStemSx = {
  width: "2px",
  height: "10px",
  mx: "auto",
  flexShrink: 0,
  backgroundColor: branchLineColor,
} as const;

// 옵션 컬럼들을 나란히 — gap 없이 둔다. 커넥터 가로선(::before/::after)이
// 컬럼 전체 폭을 덮어 인접 컬럼과 이어져야 하므로 컬럼 사이 간격은
// 컬럼 내부 콘텐츠 래퍼(px)가 담당한다.
export const taskEditorBranchColumnsRowSx = {
  display: "flex",
  alignItems: "stretch",
} as const;

// 옵션 1개 분량의 컬럼. 좌우 padding 금지 — 커넥터 가로선이 끊긴다.
// 한 번에 최대 3개만 보이므로(초과분은 페이징) 고정 minWidth 없이 균등 분할.
export const taskEditorBranchColumnSx = {
  flex: "1 1 0",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
} as const;

// 컬럼 안 콘텐츠(옵션 패널/placeholder)의 좌우 여백.
export const taskEditorBranchColumnContentSx = {
  px: 0.75,
  minWidth: 0,
} as const;

// 커넥터의 가로바만 pseudo 로 그린다: ::before = 왼쪽 절반, ::after = 오른쪽
// 절반. 첫 컬럼은 왼쪽, 마지막 컬럼은 오른쪽 절반을 제거해 바가 첫 컬럼
// 중앙~마지막 컬럼 중앙만 잇는다. 중앙 세로 stub 은 자식 Box
// (taskEditorBranchConnectorStubSx)가 담당 — 옵션 1개(first && last)면
// 가로선 없이 stub 만 남아 직선으로 degrade.
export function getTaskEditorBranchConnectorSx(
  edge: "top" | "bottom",
  isFirst: boolean,
  isLast: boolean
) {
  const borderSide = edge === "top" ? "borderTop" : "borderBottom";
  return {
    position: "relative",
    height: "14px",
    flexShrink: 0,
    "&::before": {
      content: '""',
      position: "absolute",
      [edge]: 0,
      left: 0,
      width: "50%",
      height: "100%",
      ...(isFirst ? {} : { [borderSide]: branchLine }),
    },
    "&::after": {
      content: '""',
      position: "absolute",
      [edge]: 0,
      right: 0,
      width: "50%",
      height: "100%",
      ...(isLast ? {} : { [borderSide]: branchLine }),
    },
  } as const;
}

// 커넥터 안 중앙 세로 stub — stem/filler 와 같은 중앙 정렬 방식이라
// 세로선이 정확히 일직선으로 이어진다.
export const taskEditorBranchConnectorStubSx = {
  width: "2px",
  height: "100%",
  mx: "auto",
  backgroundColor: branchLineColor,
} as const;

// 컬럼 콘텐츠 아래 ~ 하단 커넥터 사이를 채우는 세로선 — stretch 된 컬럼
// 높이를 채워 짧은 컬럼의 선도 합류 바까지 이어지게 한다.
export const taskEditorBranchColumnFillerSx = {
  flexGrow: 1,
  minHeight: "8px",
  width: "2px",
  mx: "auto",
  backgroundColor: branchLineColor,
} as const;
